import PropTypes from "prop-types";
import React from "react";

import { getAutomationDataAttr } from "@/utils/automationUtils";
import { PRETTY_MAX_FILE_SIZE, VALID_IMAGE_EXTENSIONS } from "@/utils/fileUtil";

import { createClassNameString, insuranceCompanyFilterOptions } from "../utils";
import { DateInput } from "../DateInput";
import { Dropdown } from "../Dropdown";
import { FormField } from "../FormField";
import { Input } from "../Input";
import { RadioGroup, RadioOld } from "../Radio";
import { Text } from "../Text";
import { TextTypes } from "../constants";
import { UploadFile } from "../UploadFile";

import "./styles.scss";

const childProfilePolicyHolderLabel = "Are you, the Parent/Guardian, the Policy Holder?";
const uploadFileInfoContent = `Please upload images of the front and back of your insurance card. Acceptable file formats: ${VALID_IMAGE_EXTENSIONS.join(
  ", ",
)}. Max file size: ${PRETTY_MAX_FILE_SIZE}`;

export function AddInsurance({
  className,
  errors,
  files,
  groupIdTooltipContents,
  handleBlur,
  handleChange,
  handleFocus,
  hideUploadFile,
  insuranceCompanyOptions,
  isChildProfile,
  memberIdPlaceholder,
  memberIdTip,
  memberIdTooltipContents,
  noInsurance,
  onBrowseButtonClicked,
  onFileDelete,
  policyHolderLabel,
  requiredFields,
  touched,
  values,
  ...props
}) {
  let insuranceDisabledClassName = "";

  if (noInsurance) {
    insuranceDisabledClassName = "apti-disabled";
  }

  return (
    <div className={createClassNameString(["apti-AddInsurance", className])} {...props}>
      <Text className="apti-AddInsurance--header" type={TextTypes.label}>
        Primary Insurance Information
      </Text>

      <div className="apti-AddInsurance--grid">
        <FormField
          className="apti-AddInsurance--field"
          errorMessage={touched.insuranceCompany ? errors.insuranceCompany : ""}
          errorTestId="insurance-company-error"
          label="Insurance Company"
        >
          <Dropdown
            error={touched.insuranceCompany && errors.insuranceCompany}
            filterOptions={insuranceCompanyFilterOptions}
            name="insuranceCompany"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            options={insuranceCompanyOptions}
            placeholder="Select Insurance Company"
            value={values.insuranceCompany}
            {...getAutomationDataAttr("insurance-company", "id")}
          />
        </FormField>

        <FormField
          className={createClassNameString([
            "apti-AddInsurance--field",
            insuranceDisabledClassName,
          ])}
          errorMessage={touched.policyHolder ? errors.policyHolder : ""}
          label={
            policyHolderLabel
              ? policyHolderLabel
              : isChildProfile
                ? childProfilePolicyHolderLabel
                : "Are you the Policy Holder?"
          }
        >
          <RadioGroup
            className={insuranceDisabledClassName}
            defaultValue="yes"
            disabled={noInsurance}
            name="policyHolder"
            onChange={handleChange}
            value={values.policyHolder}
          >
            <RadioOld
              label="Yes"
              onBlur={handleBlur}
              onFocus={handleFocus}
              value="yes"
              {...getAutomationDataAttr("insurance-policy-holder-yes")}
            />
            <RadioOld
              label="No"
              onBlur={handleBlur}
              onFocus={handleFocus}
              value="no"
              {...getAutomationDataAttr("insurance-policy-holder-no")}
            />
          </RadioGroup>
        </FormField>

        <FormField
          className={createClassNameString([
            "apti-AddInsurance--field",
            insuranceDisabledClassName,
          ])}
          errorMessage={touched.memberId ? errors.memberId : ""}
          errorTestId="insurance-member-id-error"
          label="Member ID"
        >
          {memberIdTip && (
            <div className="txt-gry mb-2" {...getAutomationDataAttr("insurance-member-id-tip")}>
              {memberIdTip}
            </div>
          )}

          <Input
            className={insuranceDisabledClassName}
            disabled={noInsurance}
            error={touched.memberId && errors.memberId}
            infoContent={memberIdTooltipContents}
            name="memberId"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={memberIdPlaceholder}
            testId="insurance-member-id"
            value={values.memberId}
          />
        </FormField>

        <FormField
          className={createClassNameString([
            "apti-AddInsurance--field",
            insuranceDisabledClassName,
          ])}
          errorMessage={touched.groupId ? errors.groupId : ""}
          errorTestId="insurance-group-id-error"
          label="Group ID"
          sublabel="(Optional)"
        >
          <Input
            className={insuranceDisabledClassName}
            disabled={noInsurance}
            error={touched.groupId && errors.groupId}
            infoContent={groupIdTooltipContents}
            name="groupId"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="Enter Group ID"
            testId="insurance-group-id"
            value={values.groupId}
          />
        </FormField>

        {requiredFields && requiredFields.includes("first_name") && (
          <FormField
            className="apti-AddInsurance--field"
            errorMessage={touched.firstName ? errors.firstName : ""}
            errorTestId="insurance-first-name-error"
            label="First Name"
          >
            <Input
              className={insuranceDisabledClassName}
              disabled={noInsurance}
              error={touched.firstName && errors.firstName}
              name="firstName"
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              placeholder="Enter First Name"
              testId="insurance-first-name"
              value={values.firstName}
            />
          </FormField>
        )}

        {requiredFields && requiredFields.includes("last_name") && (
          <FormField
            className="apti-AddInsurance--field"
            errorMessage={touched.lastName ? errors.lastName : ""}
            errorTestId="insurance-last-name-error"
            label="Last Name"
          >
            <Input
              className={insuranceDisabledClassName}
              disabled={noInsurance}
              error={touched.lastName && errors.lastName}
              name="lastName"
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              placeholder="Enter Last Name"
              testId="insurance-last-name"
              value={values.lastName}
            />
          </FormField>
        )}

        {requiredFields && requiredFields.includes("dob") && (
          <FormField
            className="apti-AddInsurance--field"
            errorMessage={touched.dob ? errors.dob : ""}
            errorTestId="insurance-dob-error"
            label="Date of Birth"
          >
            <DateInput
              className={insuranceDisabledClassName}
              disabled={noInsurance}
              error={touched.dob && errors.dob}
              name="dob"
              onBlur={handleBlur}
              onChange={(e) => handleChange({ target: { name: "dob", value: e.value } })}
              onFocus={handleFocus}
              placeholder="MM/DD/YYYY"
              testId="insurance-dob"
              value={values.dob}
            />
          </FormField>
        )}

        {!hideUploadFile && (
          <UploadFile
            className={createClassNameString([
              "apti-AddInsurance--upload",
              insuranceDisabledClassName,
            ])}
            disabled={noInsurance}
            files={files}
            infoContent={uploadFileInfoContent}
            onBrowseButtonClicked={onBrowseButtonClicked}
            onFileDelete={onFileDelete}
          />
        )}
      </div>
    </div>
  );
}

AddInsurance.propTypes = {
  className: PropTypes.string,
  defaultPolicyHolderAnswer: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  errors: PropTypes.object.isRequired,

  files: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  hideUploadFile: PropTypes.bool,
  insuranceCompanyOptions: PropTypes.array.isRequired,

  isChildProfile: PropTypes.bool,
  noInsurance: PropTypes.bool,
  onBrowseButtonClicked: PropTypes.func.isRequired,
  onFileDelete: PropTypes.func.isRequired,
  requiredFields: PropTypes.arrayOf(PropTypes.string),

  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};

AddInsurance.defaultProps = {
  className: undefined,
  defaultPolicyHolderAnswer: null,
  errors: {},

  files: [],
  handleBlur: () => {},
  handleChange: () => {},
  handleFocus: () => {},
  hideUploadFile: false,
  insuranceCompanyOptions: [],

  isChildProfile: false,
  noInsurance: false,
  onBrowseButtonClicked: () => {},
  onFileDelete: () => {},
  requiredFields: null,

  touched: {},
  values: {},
};
